/**
 * T317 — Exports API hooks.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { exportJobKeys } from './keys';
import { useActiveTenantId } from '../../hooks/useTenantContext';

export interface ExportJobDto {
  id: string;
  tenantId: string;
  format: 'csv' | 'datev_extf_v700' | 'zip_bundle';
  periodFrom: string;
  periodTo: string;
  expenseCount: number;
  filtersJson?: { dateBasis?: 'invoiceDate' | 'paymentDate' };
  status: 'pending' | 'running' | 'ready' | 'expired' | 'failed';
  errorMessage: string | null;
  outputByteSize: number | null;
  readyAt: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export interface CreateExportInput {
  format: 'csv' | 'datev_extf_v700' | 'zip_bundle';
  periodFrom: string;
  periodTo: string;
  statuses?: Array<'neu' | 'geprüft'>;
  includePdfs?: boolean;
  dateBasis?: 'invoiceDate' | 'paymentDate';
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

export function useExportJobs() {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: exportJobKeys.all(tenantId),
    queryFn: () => api.get<{ items: ExportJobDto[] }>('/expense-exports'),
    enabled: tenantId !== null,
  });
}

export function useCreateExportJob() {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: (input: CreateExportInput) =>
      api.post<ExportJobDto>('/expense-exports', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: exportJobKeys.all(tenantId) }),
  });
}

export function downloadExportUrl(jobId: string): string {
  return `${API_BASE}/expense-exports/${jobId}/download`;
}
